/**
 * FilterBuilder Composite Component
 *
 * A flexible, reusable filter builder component that allows users to create
 * complex filter conditions with support for multiple field types, dynamic operators,
 * AND/OR logic, and compact sidebar mode.
 *
 * Features:
 * - Add/remove filter rows dynamically
 * - Field and operator selectors with dynamic operator selection based on field type
 * - Support for string, number, date, and boolean field types
 * - AND/OR logic toggle between rows
 * - Clear all filters functionality
 * - Compact mode for sidebar use
 * - CSS variable theming (--color-*) support
 * - Full TypeScript support with exported types
 */

import * as React from 'react';
import { Plus, X, Trash2 } from 'lucide-react';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '../ui/select';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';

/** Supported field data types for filter logic */
export type FieldType = 'string' | 'number' | 'date' | 'boolean';

/** Operators available based on field type */
export type FilterOperator =
  | 'equals'
  | 'contains'
  | 'starts_with'
  | 'ends_with'
  | 'gt'
  | 'lt'
  | 'gte'
  | 'lte'
  | 'before'
  | 'after'
  | 'between'
  | 'is'
  | 'is_not';

/** Logical operator between filter rows */
export type LogicalOperator = 'AND' | 'OR';

/** Field definition for filter builder */
export interface FilterField {
  id: string;
  label: string;
  type: FieldType;
}

/** Single filter row configuration */
export interface FilterRow {
  id: string;
  fieldId: string;
  operator: FilterOperator;
  value: string | string[];
  logicalOperator?: LogicalOperator;
}

/** Props for FilterBuilder component */
export interface FilterBuilderProps {
  /** Array of available fields for filtering */
  fields: FilterField[];
  /** Current filter rows */
  filters: FilterRow[];
  /** Callback when filters change */
  onFiltersChange: (filters: FilterRow[]) => void;
  /** Show compact layout for sidebar use */
  compact?: boolean;
  /** Custom className */
  className?: string;
}

/** Operator definitions by field type */
const OPERATORS_BY_TYPE: Record<FieldType, { value: FilterOperator; label: string }[]> = {
  string: [
    { value: 'equals', label: 'Equals' },
    { value: 'contains', label: 'Contains' },
    { value: 'starts_with', label: 'Starts with' },
    { value: 'ends_with', label: 'Ends with' },
  ],
  number: [
    { value: 'equals', label: 'Equals' },
    { value: 'gt', label: 'Greater than' },
    { value: 'lt', label: 'Less than' },
    { value: 'gte', label: 'Greater or equal' },
    { value: 'lte', label: 'Less or equal' },
  ],
  date: [
    { value: 'equals', label: 'Equals' },
    { value: 'before', label: 'Before' },
    { value: 'after', label: 'After' },
    { value: 'between', label: 'Between' },
  ],
  boolean: [
    { value: 'is', label: 'Is' },
    { value: 'is_not', label: 'Is not' },
  ],
};

/** Get operators for a specific field type */
function getOperatorsForFieldType(fieldType: FieldType) {
  return OPERATORS_BY_TYPE[fieldType] || OPERATORS_BY_TYPE.string;
}

/** Get human-readable operator label */
function getOperatorLabel(operator: FilterOperator): string {
  for (const ops of Object.values(OPERATORS_BY_TYPE)) {
    const found = ops.find((op) => op.value === operator);
    if (found) return found.label;
  }
  return operator;
}

/** Generate unique ID for filter rows */
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * FilterBuilder Component
 *
 * Renders a dynamic filter interface allowing users to create complex filter conditions.
 * Supports adding/removing filter rows, changing operators based on field type, and AND/OR logic.
 */
export const FilterBuilder = React.forwardRef<
  HTMLDivElement,
  FilterBuilderProps
>(
  (
    {
      fields,
      filters,
      onFiltersChange,
      compact = false,
      className,
    },
    ref,
  ) => {
    /**
     * Add a new empty filter row
     */
    const addFilter = React.useCallback(() => {
      const newFilter: FilterRow = {
        id: generateId(),
        fieldId: fields[0]?.id || '',
        operator: 'equals',
        value: '',
        logicalOperator: filters.length > 0 ? 'AND' : undefined,
      };
      onFiltersChange([...filters, newFilter]);
    }, [filters, fields, onFiltersChange]);

    /**
     * Remove a filter row by ID
     */
    const removeFilter = React.useCallback(
      (id: string) => {
        const newFilters = filters.filter((f) => f.id !== id);
        // Clear logicalOperator from first row if it exists
        if (newFilters.length > 0) {
          newFilters[0].logicalOperator = undefined;
        }
        onFiltersChange(newFilters);
      },
      [filters, onFiltersChange],
    );

    /**
     * Update a filter row
     */
    const updateFilter = React.useCallback(
      (id: string, updates: Partial<FilterRow>) => {
        const newFilters = filters.map((f) => {
          if (f.id === id) {
            return { ...f, ...updates };
          }
          return f;
        });
        onFiltersChange(newFilters);
      },
      [filters, onFiltersChange],
    );

    /**
     * Clear all filters
     */
    const clearAllFilters = React.useCallback(() => {
      onFiltersChange([]);
    }, [onFiltersChange]);

    /**
     * Get field type for a given field ID
     */
    const getFieldType = React.useCallback(
      (fieldId: string): FieldType => {
        return fields.find((f) => f.id === fieldId)?.type || 'string';
      },
      [fields],
    );

    /**
     * Render value input based on field type and operator
     */
    const renderValueInput = (filter: FilterRow) => {
      const fieldType = getFieldType(filter.fieldId);
      const isBetween = filter.operator === 'between';
      const isBoolean = fieldType === 'boolean';

      if (isBoolean) {
        return (
          <Select
            value={String(filter.value)}
            onValueChange={(val) => updateFilter(filter.id, { value: val })}
          >
            <SelectTrigger className={compact ? 'h-8 text-xs' : ''}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="true">True</SelectItem>
              <SelectItem value="false">False</SelectItem>
            </SelectContent>
          </Select>
        );
      }

      if (isBetween) {
        const values = Array.isArray(filter.value) ? filter.value : ['', ''];
        return (
          <div className="flex items-center gap-2">
            <Input
              type={fieldType === 'date' ? 'date' : fieldType === 'number' ? 'number' : 'text'}
              placeholder="From"
              value={values[0] || ''}
              onChange={(e) =>
                updateFilter(filter.id, {
                  value: [e.target.value, values[1] || ''],
                })
              }
              className={compact ? 'h-8 text-xs' : ''}
            />
            <span className="text-[var(--color-muted-foreground)] text-sm">to</span>
            <Input
              type={fieldType === 'date' ? 'date' : fieldType === 'number' ? 'number' : 'text'}
              placeholder="To"
              value={values[1] || ''}
              onChange={(e) =>
                updateFilter(filter.id, {
                  value: [values[0] || '', e.target.value],
                })
              }
              className={compact ? 'h-8 text-xs' : ''}
            />
          </div>
        );
      }

      return (
        <Input
          type={fieldType === 'date' ? 'date' : fieldType === 'number' ? 'number' : 'text'}
          placeholder={`Enter ${fieldType} value`}
          value={Array.isArray(filter.value) ? '' : filter.value}
          onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
          className={compact ? 'h-8 text-xs' : ''}
        />
      );
    };

    return (
      <div
        ref={ref}
        className={cn(
          'space-y-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-4',
          compact && 'space-y-2 p-3',
          className,
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div
              className="h-4 w-4 rounded bg-[var(--color-primary)] opacity-70"
              aria-hidden="true"
            />
            <h3 className={cn('font-medium', compact ? 'text-sm' : '')}>
              Filters
            </h3>
          </div>
          {filters.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllFilters}
              className={cn('text-[var(--color-muted-foreground)]', compact && 'h-7 px-2 text-xs')}
            >
              <Trash2 className="h-3 w-3" />
              <span className={compact ? 'hidden sm:inline ml-1' : 'ml-1'}>Clear all</span>
            </Button>
          )}
        </div>

        {/* Filter Rows */}
        <div className="space-y-2">
          {filters.map((filter, index) => {
            const fieldType = getFieldType(filter.fieldId);
            const operators = getOperatorsForFieldType(fieldType);

            return (
              <div key={filter.id}>
                {/* Logical Operator Badge */}
                {index > 0 && filter.logicalOperator && (
                  <div className="mb-2 flex items-center gap-2">
                    <Badge
                      variant="secondary"
                      className={cn(
                        'cursor-pointer',
                        compact && 'text-xs px-1.5 py-0.5',
                      )}
                      onClick={() => {
                        updateFilter(filter.id, {
                          logicalOperator:
                            filter.logicalOperator === 'AND' ? 'OR' : 'AND',
                        });
                      }}
                    >
                      {filter.logicalOperator}
                    </Badge>
                  </div>
                )}

                {/* Filter Row */}
                <div
                  className={cn(
                    'flex items-end gap-2 rounded border border-[var(--color-input)] bg-[var(--color-muted)] p-2 opacity-90',
                    compact && 'gap-1 p-1.5',
                  )}
                >
                  {/* Field Selector */}
                  <Select value={filter.fieldId} onValueChange={(val) => {
                    const newFieldType = getFieldType(val);
                    const defaultOp = getOperatorsForFieldType(newFieldType)[0]?.value || 'equals';
                    updateFilter(filter.id, {
                      fieldId: val,
                      operator: defaultOp,
                      value: '',
                    });
                  }}>
                    <SelectTrigger className={cn('min-w-[120px]', compact && 'h-8 text-xs')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {fields.map((field) => (
                        <SelectItem key={field.id} value={field.id}>
                          {field.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Operator Selector */}
                  <Select
                    value={filter.operator}
                    onValueChange={(val) =>
                      updateFilter(filter.id, { operator: val as FilterOperator })
                    }
                  >
                    <SelectTrigger className={cn('min-w-[120px]', compact && 'h-8 text-xs')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {operators.map((op) => (
                        <SelectItem key={op.value} value={op.value}>
                          {op.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Value Input */}
                  <div className="flex-1 min-w-[100px]">
                    {renderValueInput(filter)}
                  </div>

                  {/* Remove Button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeFilter(filter.id)}
                    className={cn(
                      'text-[var(--color-muted-foreground)] hover:text-[var(--color-destructive)]',
                      compact && 'h-8 w-8',
                    )}
                  >
                    <X className="h-4 w-4" />
                    <span className="sr-only">Remove filter</span>
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Add Filter Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={addFilter}
          className={cn(
            'w-full justify-center gap-2 text-[var(--color-primary)]',
            compact && 'h-8 text-xs',
          )}
        >
          <Plus className="h-4 w-4" />
          Add Filter
        </Button>

        {/* Empty State */}
        {filters.length === 0 && (
          <div className={cn(
            'text-center py-6 text-[var(--color-muted-foreground)]',
            compact && 'py-3 text-xs',
          )}>
            <p>No filters applied</p>
          </div>
        )}
      </div>
    );
  },
);

FilterBuilder.displayName = 'FilterBuilder';
